import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Plus, MapPin, ArrowRight, Trash, UploadSimple, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

function ThumbnailPicker({ value, onChange }) {
  const [mode, setMode] = useState("preset"); // preset | upload | url
  const [urlInput, setUrlInput] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/projects/upload-thumbnail", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Use absolute URL so the <img src> can fetch it through the backend route
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(abs);
      toast.success("Thumbnail uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid="thumbnail-picker">
      <div className="flex gap-1 p-1 bg-zinc-100 rounded-md text-xs font-semibold uppercase tracking-wide">
        {[
          { id: "preset", label: "Presets" },
          { id: "upload", label: "Upload" },
          { id: "url", label: "URL" },
        ].map((t) => (
          <button
            type="button"
            key={t.id}
            onClick={() => setMode(t.id)}
            className={`flex-1 py-1.5 rounded-sm transition-colors ${
              mode === t.id ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`thumb-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {mode === "preset" && (
        <div className="grid grid-cols-2 gap-2">
          {DEFAULT_THUMBS.map((url) => (
            <button
              type="button"
              key={url}
              onClick={() => onChange(url)}
              className={`h-20 rounded-sm overflow-hidden border-2 transition-all ${
                value === url ? "border-[hsl(var(--accent))]" : "border-transparent"
              }`}
              data-testid={`thumb-option-${url.slice(-20)}`}
            >
              <img src={url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {mode === "upload" && (
        <div>
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="thumb-file-input" />
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 rounded-md text-xs uppercase tracking-wide font-semibold"
            data-testid="thumb-upload-button"
          >
            <UploadSimple size={14} weight="bold" className="mr-2" />
            {busy ? "Uploading..." : "Choose image from computer"}
          </Button>
          <p className="text-xs text-muted-foreground mt-1.5 text-center">PNG, JPG, WEBP — up to 8MB</p>
        </div>
      )}

      {mode === "url" && (
        <div className="flex gap-2">
          <Input
            placeholder="https://example.com/image.jpg"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            data-testid="thumb-url-input"
          />
          <Button
            type="button"
            onClick={() => { if (urlInput) { onChange(urlInput); toast.success("Thumbnail set"); } }}
            className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 rounded-md text-xs uppercase tracking-wide font-semibold"
            data-testid="thumb-url-apply"
          >
            <LinkSimple size={14} weight="bold" />
          </Button>
        </div>
      )}

      {value && (
        <div className="rounded-sm overflow-hidden border border-border h-28">
          <img src={value} alt="Preview" className="w-full h-full object-cover" data-testid="thumb-preview" />
        </div>
      )}
    </div>
  );
}

const DEFAULT_THUMBS = [
  "https://images.unsplash.com/photo-1599707254554-027aeb4deacd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.pexels.com/photos/2383650/pexels-photo-2383650.jpeg?auto=compress&cs=tinysrgb&w=1200",
];

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const canManage = !(user?.role === "contractor" && (user?.team_access || "full") !== "full");

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/projects"),
        api.get("/projects/stats").catch(() => ({ data: {} })),
      ]);
      setProjects(p.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <AppShell>
      <div className="space-y-8" data-testid="projects-page">
        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="label-eyebrow text-[#007BFF]">Workspace</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#001E44] mt-2">
              Projects
            </h1>
            <p className="text-zinc-500 mt-2 text-sm sm:text-base">
              Every construction site you have access to.
            </p>
          </div>
          {canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#007BFF] hover:bg-[#0062cc] rounded-full font-semibold text-sm px-5 py-2.5 h-auto shadow-sm hover:shadow-md transition-all"
                  data-testid="new-project-button"
                >
                  <Plus size={16} weight="bold" className="mr-2" /> New project
                </Button>
              </DialogTrigger>
              <NewProjectDialog onCreated={() => { setOpen(false); load(); }} />
            </Dialog>
          )}
        </header>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => (
              <div key={`proj-skel-${i}`} className="ctrl-card h-64 bg-zinc-100 animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="ctrl-card p-12 text-center">
            <span className="label-eyebrow">No projects yet</span>
            <p className="font-display text-2xl font-bold tracking-tight mt-2">
              Spin up your first construction site.
            </p>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Projects are the home for contractors and all of their procurement items.
            </p>
            {canManage && (
              <Button
                onClick={() => setOpen(true)}
                className="mt-6 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 rounded-md font-semibold uppercase tracking-wide text-xs"
                data-testid="empty-create-project-button"
              >
                <Plus size={16} weight="bold" className="mr-2" /> Create project
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                stats={stats[p.id]}
                canManage={canManage}
                canDelete={user?.role === "admin" || p.owner_id === user?.id}
                onDeleted={load}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function ProjectCard({ project, stats, canManage, canDelete, onDeleted }) {
  const overdueCount = stats?.overdue_count || 0;
  const isOverdue = overdueCount > 0;

  const handleDelete = async () => {
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success("Project deleted");
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div
      className={`ctrl-card overflow-hidden group ${isOverdue ? "glow-overdue" : ""}`}
      data-testid={`project-card-${project.id}`}
    >
      <Link to={`/projects/${project.id}`} className="block">
        <div className="h-44 overflow-hidden relative bg-zinc-100">
          <img
            src={project.thumbnail_url || DEFAULT_THUMBS[0]}
            alt={project.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute top-3 left-3 bg-white/90 text-[#001E44] text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full font-semibold backdrop-blur-sm">
            Active
          </div>
          {isOverdue && (
            <span
              className="absolute top-3 right-3 chip bg-red-600 text-white shadow-md"
              data-testid={`project-overdue-badge-${project.id}`}
            >
              {overdueCount} overdue
            </span>
          )}
        </div>
      </Link>
      <div className="p-5">
        <Link to={`/projects/${project.id}`}>
          <h3 className="font-display text-lg font-semibold tracking-tight text-[#001E44] hover:text-[#007BFF] transition-colors">
            {project.name}
          </h3>
        </Link>
        <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-2">
          <MapPin size={12} weight="bold" />
          <span>{project.location || "No location set"}</span>
        </div>
        {project.description && (
          <p className="text-sm text-zinc-500 mt-3 line-clamp-2 leading-relaxed">{project.description}</p>
        )}
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-zinc-100">
          <Link
            to={`/projects/${project.id}`}
            className="text-xs font-semibold text-[#007BFF] hover:underline inline-flex items-center gap-1"
            data-testid={`open-project-${project.id}`}
          >
            Open <ArrowRight size={12} weight="bold" />
          </Link>
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  className="text-zinc-400 hover:text-destructive transition-colors p-1"
                  data-testid={`delete-project-${project.id}`}
                  title="Delete"
                >
                  <Trash size={16} weight="bold" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete project?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove &ldquo;{project.name}&rdquo; along with all contractor accounts
                    and procurement items inside it.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-delete-project">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-project">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
    </div>
  );
}

function NewProjectDialog({ onCreated }) {
  const [form, setForm] = useState({
    name: "", location: "", description: "",
    thumbnail_url: DEFAULT_THUMBS[0],
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md" data-testid="new-project-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold tracking-tight">New project</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Name</Label>
          <Input
            required value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Downtown Tower Phase 2"
            data-testid="project-name-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Location</Label>
          <Input
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="e.g. 142 Main St, Chicago"
            data-testid="project-location-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Description</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={3}
            placeholder="What's being built here?"
            data-testid="project-description-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="label-eyebrow">Thumbnail</Label>
          <ThumbnailPicker
            value={form.thumbnail_url}
            onChange={(url) => setForm((f) => ({ ...f, thumbnail_url: url }))}
          />
        </div>
        <DialogFooter className="pt-2">
          <Button
            type="submit" disabled={busy}
            className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
            data-testid="submit-new-project"
          >
            {busy ? "Creating..." : "Create project"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
